import { SEO } from "@/components/common/SEO";
import { Header } from "@/components/learn-more/Header";
import { Footer } from "@/components/learn-more/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { generateFAQStructuredData } from "@/utils/seoUtils";
import { useRef, useEffect } from "react";

export default function Resources() {
  const navigate = useNavigate();
  const faqRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Check if URL has #faq hash and scroll to FAQ section
    if (window.location.hash === '#faq' && faqRef.current) {
      faqRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);
  
  const faqItems = [
    {
      question: "What features does Hour Tracker offer?",
      answer: "Hour Tracker provides comprehensive time tracking, project management, and invoice generation tools. You can create time entries manually or use our timer feature, organize work by project and client, and generate professional invoices with a single click."
    },
    {
      question: "How do I get started with Hour Tracker?",
      answer: "Getting started is simple! Create an account, add your first project, and start tracking time. You can create time entries manually or use our timer feature. When it's time to bill your client, generate a professional invoice with one click."
    },
    {
      question: "Can I track time for multiple projects and clients?",
      answer: "Absolutely! Hour Tracker allows you to create projects and organize them by client. Each project can have its own hourly rate, making it easy to track earnings across different types of work."
    },
    {
      question: "How do invoices work in Hour Tracker?",
      answer: "Hour Tracker automatically generates professional invoices based on your tracked time. Simply select the date range and projects you want to include, and the system will create a detailed invoice that you can download as a PDF and send to your clients."
    },
    {
      question: "Is my data secure with Hour Tracker?",
      answer: "Yes, we take data security very seriously. Hour Tracker uses industry-standard encryption and secure authentication methods. Your data is stored securely and is never shared with third parties without your consent."
    },
    {
      question: "Can I access Hour Tracker on my mobile device?",
      answer: "Yes! Hour Tracker is fully responsive and works on all devices including smartphones and tablets. You can track time, manage projects, and even generate invoices while on the go."
    },
    {
      question: "How do I contact support if I need help?",
      answer: "If you need assistance, you can reach our support team at support@hourtracker.info. We typically respond within 24 hours to all inquiries."
    },
    {
      question: "Can I export my data from Hour Tracker?",
      answer: "Yes, you can export your time entries and invoice data at any time. Hour Tracker supports PDF exports for invoices and CSV exports for time tracking data, making it easy to use your information in other systems."
    }
  ];
  
  const faqStructuredData = generateFAQStructuredData(faqItems);
  
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Resources & FAQ | Hour Tracker Time Tracking Software"
        description="Find helpful resources and answers to frequently asked questions about Hour Tracker, the time tracking and invoicing tool for freelancers and small businesses."
        keywords="hour tracker faq, freelancer time tracking help, invoice generator guide, billable hours tips, time tracking tutorial"
        structuredData={faqStructuredData}
      />
      
      <Header />
      
      <main className="flex-1 py-8">
        <div className="container max-w-4xl">
          <h1 className="text-3xl font-bold mb-6 text-center">Resources</h1>
          
          <Tabs defaultValue="faq" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="faq">FAQ</TabsTrigger>
              <TabsTrigger value="blog">Blog</TabsTrigger>
            </TabsList>
            
            <TabsContent value="faq">
              <div ref={faqRef} className="mb-10">
                <h2 className="text-2xl font-bold mb-6" id="faq">Frequently Asked Questions</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqItems.map((item, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left font-medium">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground">{item.answer}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                
                <div className="mt-8 text-center">
                  <p className="text-muted-foreground mb-4">
                    Didn't find what you're looking for? We're here to help!
                  </p>
                  <Button onClick={() => window.location.href = "mailto:support@hourtracker.info"}>
                    Contact Support
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="blog">
              <div className="text-center py-12">
                <h2 className="text-2xl font-bold mb-4">Freelancer Resources</h2>
                <p className="text-muted-foreground mb-8">
                  We're working on helpful articles to help you maximize your freelance business. 
                  Check back soon for tips and strategies!
                </p>
                <Button onClick={() => navigate("/auth?mode=signup")}>
                  Try Hour Tracker Now
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
